import {
  AT_RISK_STATUS,
  getPaeQueueDisplayStatus,
  isSalesCycleStatus,
  NEEDS_REVIEW_LABEL,
} from './partnerProjectStatusUtils';
import { PARTNER_PRIMARY_STATUSES } from '../Constants/partnerStatuses';

const toAlertCount = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const PAE_QUEUE_STAT_CARD_FILTERS = {
  ACTIVE: PARTNER_PRIMARY_STATUSES.ACTIVE,
  AT_RISK: AT_RISK_STATUS,
  COMPLETED: PARTNER_PRIMARY_STATUSES.COMPLETED,
  NEEDS_REVIEW: NEEDS_REVIEW_LABEL,
  SALES_CYCLE: PARTNER_PRIMARY_STATUSES.NOT_STARTED,
};

export const matchesPaeQueueStatCardFilter = (project, selectedStatus) => {
  if (!selectedStatus || selectedStatus === 'All') {
    return true;
  }

  const displayStatus = getPaeQueueDisplayStatus(project);

  switch (selectedStatus) {
    case PAE_QUEUE_STAT_CARD_FILTERS.ACTIVE:
      return displayStatus === PARTNER_PRIMARY_STATUSES.ACTIVE;
    case PAE_QUEUE_STAT_CARD_FILTERS.AT_RISK:
      return displayStatus === AT_RISK_STATUS;
    case PAE_QUEUE_STAT_CARD_FILTERS.COMPLETED:
      return displayStatus === PARTNER_PRIMARY_STATUSES.COMPLETED;
    case PAE_QUEUE_STAT_CARD_FILTERS.NEEDS_REVIEW:
      return displayStatus === NEEDS_REVIEW_LABEL;
    case PAE_QUEUE_STAT_CARD_FILTERS.SALES_CYCLE:
      return isSalesCycleStatus(displayStatus);
    default:
      return displayStatus === selectedStatus;
  }
};

export const countPaeQueueStatCards = (projects = []) => (
  projects.reduce((accumulator, project) => {
    const displayStatus = getPaeQueueDisplayStatus(project);

    if (displayStatus === PARTNER_PRIMARY_STATUSES.ACTIVE) {
      accumulator.active += 1;
    } else if (displayStatus === AT_RISK_STATUS) {
      accumulator.atRisk += 1;
    } else if (displayStatus === PARTNER_PRIMARY_STATUSES.COMPLETED) {
      accumulator.completed += 1;
    } else if (displayStatus === NEEDS_REVIEW_LABEL) {
      accumulator.needsReview += 1;
    } else if (isSalesCycleStatus(displayStatus)) {
      accumulator.salesCycle += 1;
    }

    return accumulator;
  }, {
    active: 0,
    atRisk: 0,
    completed: 0,
    needsReview: 0,
    salesCycle: 0,
  })
);

const normalizeStatusText = (value = '') => String(value || '').trim().toLowerCase();

const normalizeProjectStatus = (project, fallbackStatus = '') => (
  project?.projectStatus ?? fallbackStatus
);

export const PSM_QUEUE_STATUS_GROUPS = {
  NEEDS_REVIEW: 'needsReview',
  AT_RISK: 'atRisk',
  INFO_REQUESTED: 'infoRequested',
  ACTIVE: 'active',
  GO_LIVE_COMPLETE: 'goLiveComplete',
  COMPLETED: 'completed',
  OTHER: 'other',
};

export const getPsmQueueStatusGroup = (projectOrStatus) => {
  const project = typeof projectOrStatus === 'object' ? projectOrStatus : null;
  const status = normalizeProjectStatus(project, projectOrStatus);
  const normalizedStatus = normalizeStatusText(status);

  if (normalizedStatus.includes('at risk')) {
    return PSM_QUEUE_STATUS_GROUPS.AT_RISK;
  }

  if (normalizedStatus.includes('info requested')) {
    return PSM_QUEUE_STATUS_GROUPS.INFO_REQUESTED;
  }

  if (normalizedStatus.includes('active')) {
    return PSM_QUEUE_STATUS_GROUPS.ACTIVE;
  }

  if (
    normalizedStatus.includes('go-live complete')
    || normalizedStatus.includes('go live complete')
    || normalizedStatus.includes('golive complete')
  ) {
    return PSM_QUEUE_STATUS_GROUPS.GO_LIVE_COMPLETE;
  }

  if (normalizedStatus.includes('completed')) {
    return PSM_QUEUE_STATUS_GROUPS.COMPLETED;
  }

  if (normalizedStatus.includes('sales cycle')) {
    const hasApproval = typeof project?.paeApproved === 'boolean'
      ? project.paeApproved
      : (Array.isArray(project?.activityLog)
        ? project.activityLog.some(
          (entry) => String(entry?.action || '').trim().toLowerCase() === 'approved',
        )
        : false);
    return hasApproval
      ? PSM_QUEUE_STATUS_GROUPS.OTHER
      : PSM_QUEUE_STATUS_GROUPS.NEEDS_REVIEW;
  }

  return PSM_QUEUE_STATUS_GROUPS.OTHER;
};

export const EMPTY_PSM_QUEUE_ALERTS = {
  needsReview: 0,
  atRisk: 0,
  infoRequested: 0,
  approved: 0,
};

export const normalizePSMQueueAlerts = (alerts = {}) => ({
  needsReview: toAlertCount(
    alerts.needsReview
    ?? alerts.needs_review
    ?? alerts.needsReviewCount
    ?? alerts.needs_review_count,
  ),
  atRisk: toAlertCount(
    alerts.atRisk
    ?? alerts.at_risk
    ?? alerts.atRiskCount
    ?? alerts.at_risk_count,
  ),
  infoRequested: toAlertCount(
    alerts.infoRequested
    ?? alerts.info_requested
    ?? alerts.infoRequestedCount
    ?? alerts.info_requested_count,
  ),
  approved: toAlertCount(
    alerts.approved
    ?? alerts.approvedCount
    ?? alerts.approved_count,
  ),
});

const isRecord = (value) => value && typeof value === 'object' && !Array.isArray(value);

const pickAlertsSource = (...candidates) => (
  candidates.find(isRecord) || {}
);

const pickProjects = (...candidates) => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
};

export const normalizePSMQueueResponse = (data) => {
  if (!data || typeof data !== 'object') {
    return { projects: [], alerts: EMPTY_PSM_QUEUE_ALERTS };
  }

  if (Array.isArray(data)) {
    return {
      projects: data,
      alerts: EMPTY_PSM_QUEUE_ALERTS,
    };
  }

  const nested = isRecord(data.data) ? data.data : null;
  const result = isRecord(data.result) ? data.result : null;
  const queue = isRecord(data.queue) ? data.queue : null;

  const projects = pickProjects(
    data.projects,
    nested?.projects,
    result?.projects,
    queue?.projects,
  );

  const alertsSource = pickAlertsSource(
    data.alerts,
    nested?.alerts,
    result?.alerts,
    queue?.alerts,
    data.summary,
    nested?.summary,
    result?.summary,
    data.counts,
    nested?.counts,
    result?.counts,
  );

  return {
    projects,
    alerts: normalizePSMQueueAlerts(alertsSource),
  };
};
